import { acquireAdSlot, deferAdSlot } from './adSlot';
import { adEnvSnapshot, logAd } from './adDiagnostics';
import { adsRuntimeReady } from './adsRuntime';

export const ADSGRAM_REWARDED_BLOCK_ID = '48373';
export const ADSGRAM_INTERSTITIAL_BLOCK_ID = 'int-48374';

const ADSGRAM_SDK_URL = 'https://sad.adsgram.ai/js/sad.min.js';
const INTERSTITIAL_LAUNCH_FALLBACK_MS = 3_000;
const TERMINAL_WATCHDOG_MS = 2_000;
const HANDOFF_FALLBACK_MS = 15_000;
// One shared cooldown for every AdsGram entry point, matching postingBot's
// nextNetworkAdAtRef. Startup and external-link flows must not keep separate clocks.
const REPEAT_AD_COOLDOWN_MS = 100_000;
// Пауза после цепочки, чтобы фоновый шедулер не стартовал впритык к её завершению.
const AD_SLOT_COOLDOWN_MS = 10_000;
// Реклама по кнопке пользователя замок не занимает, но отодвигает фоновые показы.
const USER_AD_COOLDOWN_MS = 60_000;
// Сколько ждём завершения фоновой цепочки, прежде чем показать rewarded по кнопке.
const USER_AD_WAIT_MS = 10_000;

type AdsgramEvent =
  | 'onStart'
  | 'onSkip'
  | 'onComplete'
  | 'onError'
  | 'onBannerNotFound'
  | 'onNonStopShow'
  | 'onTooLongSession';

interface AdsgramController {
  show: () => Promise<unknown>;
  addEventListener?: (event: AdsgramEvent, listener: () => void) => void;
  removeEventListener?: (event: AdsgramEvent, listener: () => void) => void;
}

interface AdsgramSdk {
  init: (options: { blockId: string; debug?: boolean; showErrors?: boolean }) => AdsgramController;
}

export interface AdsgramShowResult {
  status: 'completed' | 'skipped';
  reason?: string;
}

export interface AdsgramSequenceOptions {
  trigger: string;
  onInterstitialUnavailable?: () => void;
  onInterstitialFinished?: () => void;
}

interface SequenceHandoff {
  onUnavailable?: () => void;
  onFinished?: () => void;
}

interface SequenceState {
  interstitialStarted: boolean;
  settled: boolean;
  waiters: SequenceHandoff[];
}

let sdkPromise: Promise<AdsgramSdk> | null = null;
const controllerPromises = new Map<string, Promise<AdsgramController>>();
let activeSequence: Promise<void> | null = null;
let activeState: SequenceState | null = null;
let nextSequenceAt = 0;

function getSdk(): AdsgramSdk | null {
  const sdk = (window as any).Adsgram;
  return sdk && typeof sdk.init === 'function' ? sdk as AdsgramSdk : null;
}

function appendAdsgramScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = ADSGRAM_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error(`adsgram_sdk_load_failed:${ADSGRAM_SDK_URL}`));
    };
    document.head.appendChild(script);
  });
}

export function getAdsgramSdk(): Promise<AdsgramSdk> {
  if (sdkPromise) return sdkPromise;

  sdkPromise = (async () => {
    // Скрипт совместимости должен встать раньше SDK, иначе первый показ уйдёт в
    // «свёрнутое» окно и Adsgram посчитает сессию неактивной.
    await adsRuntimeReady();
    let sdk = getSdk();
    if (!sdk) {
      await appendAdsgramScript();
      sdk = getSdk();
    }
    if (!sdk) throw new Error('adsgram_unavailable');
    return sdk;
  })().catch((error) => {
    sdkPromise = null;
    throw error;
  });

  return sdkPromise;
}

export function getAdsgramController(blockId: string): Promise<AdsgramController> {
  const cached = controllerPromises.get(blockId);
  if (cached) return cached;

  const controllerPromise = getAdsgramSdk()
    .then((sdk) => {
      const controller = sdk.init({ blockId });
      // Only documented lifecycle events are registered, exactly as in
      // postingBot. Registering an unsupported event may reject controller
      // initialization in some Adsgram SDK versions.
      for (const event of ['onBannerNotFound', 'onNonStopShow', 'onTooLongSession'] as AdsgramEvent[]) {
        controller.addEventListener?.(event, () => {});
      }
      return controller;
    })
    .catch((error) => {
      controllerPromises.delete(blockId);
      throw error;
    });

  controllerPromises.set(blockId, controllerPromise);
  return controllerPromise;
}

export async function showAdsgramAd(
  blockId: string,
  onStarted?: () => void,
): Promise<AdsgramShowResult> {
  let controller: AdsgramController | null = null;
  let started = false;
  let skipped = false;
  let terminalTimer: number | undefined;
  let resolveTerminal: ((reason: string) => void) | undefined;
  const requestedAt = Date.now();

  const handleStart = () => {
    if (started) return;
    started = true;
    // Снимок окружения именно в момент старта показа: по нему видно, был ли
    // мини-апп свёрнут и сработала ли подмена видимости/геометрии.
    logAd('ok', blockId, 'onStart', adEnvSnapshot());
    onStarted?.();
  };
  const handleSkip = () => {
    skipped = true;
    armTerminal('onSkip');
  };
  const terminal = new Promise<string>((resolve) => { resolveTerminal = resolve; });
  const armTerminal = (reason: string) => {
    if (terminalTimer) return;
    terminalTimer = window.setTimeout(() => resolveTerminal?.(reason), TERMINAL_WATCHDOG_MS);
  };
  const terminalListeners: Partial<Record<AdsgramEvent, () => void>> = {
    onComplete: () => armTerminal('onComplete'),
    onError: () => armTerminal('onError'),
    onBannerNotFound: () => armTerminal('onBannerNotFound'),
    onNonStopShow: () => armTerminal('onNonStopShow'),
    onTooLongSession: () => armTerminal('onTooLongSession'),
  };

  try {
    // Скрипт совместимости мог не успеть к загрузке SDK (авторизация идёт по сети).
    // Его правки глобальные и читаются AdsGram на каждом тике показа, поэтому
    // достаточно дождаться его здесь — тогда даже опоздавший скрипт отработает.
    await adsRuntimeReady();
    controller = await getAdsgramController(blockId);
    controller.addEventListener?.('onStart', handleStart);
    controller.addEventListener?.('onSkip', handleSkip);
    Object.entries(terminalListeners).forEach(([event, handler]) => {
      controller?.addEventListener?.(event as AdsgramEvent, handler!);
    });
    logAd('info', blockId, 'show()');
    const outcome = await Promise.race([
      controller.show().then(() => 'settled'),
      terminal,
    ]);
    if (outcome !== 'settled') {
      return { status: 'skipped', reason: outcome };
    }
    logAd('ok', blockId, `завершён за ${Date.now() - requestedAt}мс`, { started });
    return { status: 'completed' };
  } catch (error: any) {
    const reason = error?.description || error?.message || 'no_ad_available';
    if (/timeout/i.test(reason)) controllerPromises.delete(blockId);
    const result: AdsgramShowResult = { status: 'skipped', reason: skipped ? 'skipped' : reason };
    logAd(started ? 'warn' : 'info', blockId, `не засчитан: ${result.reason}`, {
      startedBeforeFail: started,
      elapsedMs: Date.now() - requestedAt,
    });
    return result;
  } finally {
    if (terminalTimer) window.clearTimeout(terminalTimer);
    controller?.removeEventListener?.('onStart', handleStart);
    controller?.removeEventListener?.('onSkip', handleSkip);
    Object.entries(terminalListeners).forEach(([event, handler]) => {
      controller?.removeEventListener?.(event as AdsgramEvent, handler!);
    });
  }
}

export const showAdsgramRewarded = (onStarted?: () => void) =>
  showAdsgramAd(ADSGRAM_REWARDED_BLOCK_ID, onStarted);

export const showAdsgramInterstitial = (onStarted?: () => void) =>
  showAdsgramAd(ADSGRAM_INTERSTITIAL_BLOCK_ID, onStarted);

// Показ по явному действию пользователя («Смотреть рекламу»). Замок не берём —
// пользователь ждёт награду прямо сейчас, — но фоновые показы придерживаем.
export async function showAdsgramRewardedForUser(): Promise<AdsgramShowResult> {
  deferAdSlot(USER_AD_COOLDOWN_MS);
  try {
    // Фоновая цепочка может прямо сейчас крутить ТОТ ЖЕ rewarded-блок: SDK ответит
    // "Attempt to call show when an ad is being shown", юнит сгорит впустую, а
    // пользователь увидит «нет рекламы». Пропускаем её вперёд — deferAdSlot выше уже
    // гарантирует, что новая фоновая цепочка после неё не стартует.
    await waitForSequenceIdle(USER_AD_WAIT_MS);
    return await showAdsgramRewarded();
  } finally {
    deferAdSlot(USER_AD_COOLDOWN_MS);
  }
}

export function preloadAdsgramControllers(): Promise<PromiseSettledResult<AdsgramController>[]> {
  return Promise.allSettled([
    getAdsgramController(ADSGRAM_REWARDED_BLOCK_ID),
    getAdsgramController(ADSGRAM_INTERSTITIAL_BLOCK_ID),
  ]);
}

export function isAdsgramSequenceActive(): boolean {
  return activeSequence !== null;
}

export function getAdsgramSequenceCooldownMs(): number {
  return Math.max(0, nextSequenceAt - Date.now());
}

/** Ждёт завершения текущей цепочки, но не дольше указанного срока. */
function waitForSequenceIdle(timeoutMs: number): Promise<void> {
  const sequence = activeSequence;
  if (!sequence) return Promise.resolve();
  return Promise.race([
    sequence.catch(() => {}),
    new Promise<void>((resolve) => window.setTimeout(resolve, timeoutMs)),
  ]);
}

// Рабочая схема postingBot: rewarded и interstitial готовятся параллельно;
// переход по офферу происходит, когда interstitial реально стартовал.
export function startAdsgramSequence(options: AdsgramSequenceOptions): boolean {
  // Цепочка уже идёт (обычно — фоновая стартовая). Раньше здесь возвращался false,
  // и вызывающий уходил по ссылке вообще без рекламы: фоновый цикл занимает экран
  // заметную часть времени, поэтому «реклама по ссылке» на практике не показывалась
  // почти никогда. Вместо отказа подключаем переход к уже идущему показу — ровно так
  // это устроено в postingBot (queueNetworkAdHandoff).
  if (activeSequence && activeState) {
    const state = activeState;
    if (state.interstitialStarted) {
      logAd('info', 'chain', `подключение к цепочке (${options.trigger}): interstitial уже идёт`);
      state.waiters.push({ onFinished: options.onInterstitialFinished });
    } else if (state.settled) {
      logAd('info', 'chain', `подключение к цепочке (${options.trigger}): показа не было`);
      options.onInterstitialUnavailable?.();
    } else {
      logAd('info', 'chain', `подключение к цепочке (${options.trigger}): ждём interstitial`);
      state.waiters.push({
        onUnavailable: options.onInterstitialUnavailable,
        onFinished: options.onInterstitialFinished,
      });
    }
    return true;
  }

  // postingBot checks its shared nextNetworkAdAtRef before every new chain.
  // Without this, Tapalka's startup and link modules could each start their own
  // interstitial during the same rewarded session.
  if (Date.now() < nextSequenceAt) {
    return false;
  }

  const releaseSlot = acquireAdSlot(`adsgram:${options.trigger}`);
  if (!releaseSlot) return false;

  logAd('info', 'chain', `старт цепочки (${options.trigger})`, adEnvSnapshot());

  const state: SequenceState = { interstitialStarted: false, settled: false, waiters: [] };
    if (options.onInterstitialUnavailable || options.onInterstitialFinished) {
      state.waiters.push({
        onUnavailable: options.onInterstitialUnavailable,
        onFinished: options.onInterstitialFinished,
      });
  }

  let interstitialStarted = false;
  let interstitialLaunched = false;
  let launchTimer: number | undefined;
  let handoffTimer: number | undefined;

  // Переход отдаём всем, кто ждёт этой цепочки: и тому, кто её запустил, и тем,
  // кто подключился к ней позже. Повторные вызовы безопасны — очередь пустеет.
  const handoff = (available: boolean) => {
    if (state.settled) return;
    state.settled = true;
    state.interstitialStarted = available;
    logAd('info', 'chain', available ? 'переход по офферу (interstitial пошёл)' : 'переход по офферу (без рекламы)');
    const waiters = state.waiters.splice(0);
    const pending = [] as SequenceHandoff[];
    for (const waiter of waiters) {
      try {
        if (!available) waiter.onUnavailable?.();
        if (available && waiter.onFinished) pending.push(waiter);
      } catch (error) {
        logAd('error', 'chain', 'обработчик перехода упал', { error: String(error) });
      }
    }
    state.waiters.push(...pending);
  };

  const complete = () => {
    if (launchTimer) window.clearTimeout(launchTimer);
    if (handoffTimer) window.clearTimeout(handoffTimer);
    if (!interstitialStarted) handoff(false);
    if (interstitialStarted) {
      const waiters = state.waiters.splice(0);
      for (const waiter of waiters) {
        try { waiter.onFinished?.(); } catch (error) {
          logAd('error', 'chain', 'terminal handler failed', { error: String(error) });
        }
      }
    }
    nextSequenceAt = Date.now() + REPEAT_AD_COOLDOWN_MS;
    logAd('info', 'chain', 'цепочка завершена', adEnvSnapshot());
    releaseSlot(AD_SLOT_COOLDOWN_MS);
  };

  let resolveInterstitial: () => void = () => {};
  const interstitialSettled = new Promise<void>((resolve) => {
    resolveInterstitial = resolve;
  });
  const sequence = interstitialSettled.finally(complete);
  activeSequence = sequence;
  activeState = state;
  void sequence.finally(() => {
    if (activeSequence === sequence) {
      activeSequence = null;
      activeState = null;
    }
  });

  const launchInterstitial = () => {
    if (interstitialLaunched) return;
    interstitialLaunched = true;
    if (launchTimer) window.clearTimeout(launchTimer);

    void showAdsgramInterstitial(() => {
      interstitialStarted = true;
      handoff(true);
    }).finally(resolveInterstitial);
  };

  launchTimer = window.setTimeout(launchInterstitial, INTERSTITIAL_LAUNCH_FALLBACK_MS);
  handoffTimer = window.setTimeout(() => handoff(false), HANDOFF_FALLBACK_MS);

  // Не ждём завершения rewarded: в postingBot активность цепочки заканчивается
  // после interstitial, а rewarded продолжает свой отсчёт параллельно.
  void showAdsgramRewarded(launchInterstitial).finally(launchInterstitial);
  return true;
}
